import { useState, useCallback, useEffect } from 'react';
import { useDatabase } from '../db';
import type { StockAdjustmentDocType, ProductDocType } from '@lancar-pos/db-schema';

export type StockAdjustment = StockAdjustmentDocType;

export function useStockAdjustmentStore() {
  const db = useDatabase();
  const [stockAdjustments, setStockAdjustments] = useState<StockAdjustment[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    const subscription = db.stock_adjustments
      .find({
        selector: { _deleted: false },
        sort: [{ createdAt: 'desc' }],
      })
      .$.subscribe((docs: Array<{ toMutableJSON: () => StockAdjustment }>) => {
        setStockAdjustments(docs.map((doc: { toMutableJSON: () => StockAdjustment }) => doc.toMutableJSON() as StockAdjustment));
        setIsLoading(false);
      });

    return () => subscription.unsubscribe();
  }, [db.stock_adjustments]);

  const addStockAdjustment = useCallback(
    async (adjustment: Omit<StockAdjustment, 'id' | 'createdAt' | 'updatedAt' | '_deleted'>) => {
      const now = new Date().toISOString();
      await db.stock_adjustments.insert({
        ...adjustment,
        id: crypto.randomUUID(),
        createdAt: now,
        updatedAt: now,
        _deleted: false,
      });
    },
    [db.stock_adjustments]
  );

  const updateStockAdjustment = useCallback(
    async (id: string, updates: Partial<Omit<StockAdjustment, 'id' | 'createdAt'>>) => {
      const doc = await db.stock_adjustments.findOne(id).exec();
      if (doc) {
        await doc.patch({
          ...updates,
          updatedAt: new Date().toISOString(),
        });
      }
    },
    [db.stock_adjustments]
  );

  const deleteStockAdjustment = useCallback(
    async (id: string) => {
      const doc = await db.stock_adjustments.findOne(id).exec();
      if (doc) {
        await doc.patch({
          _deleted: true,
          updatedAt: new Date().toISOString(),
        });
      }
    },
    [db.stock_adjustments]
  );

  const getStockAdjustmentById = useCallback(
    (id: string): StockAdjustment | undefined => {
      return stockAdjustments.find((sa) => sa.id === id);
    },
    [stockAdjustments]
  );

  const approveAdjustment = useCallback(
    async (id: string, approvedBy: string) => {
      const doc = await db.stock_adjustments.findOne(id).exec();
      if (!doc) return;

      const adjustment = doc.toMutableJSON() as StockAdjustment;
      
      const product = await db.products.findOne(adjustment.productId).exec();
      if (!product) return;

      const currentProduct = product.toMutableJSON() as ProductDocType;
      let newStock = currentProduct.stock;

      if (adjustment.adjustmentType === 'increase') {
        newStock = currentProduct.stock + adjustment.quantity;
      } else {
        newStock = currentProduct.stock - adjustment.quantity;
      }

      if (newStock < 0) {
        throw new Error('Stock tidak mencukupi');
      }

      await product.patch({
        stock: newStock,
        updatedAt: new Date().toISOString(),
      });

      await doc.patch({
        status: 'approved',
        approvedBy,
        updatedAt: new Date().toISOString(),
      });
    },
    [db.stock_adjustments, db.products]
  );

  const rejectAdjustment = useCallback(
    async (id: string, rejectedBy: string) => {
      const doc = await db.stock_adjustments.findOne(id).exec();
      if (doc) {
        await doc.patch({
          status: 'rejected',
          approvedBy: rejectedBy,
          updatedAt: new Date().toISOString(),
        });
      }
    },
    [db.stock_adjustments]
  );

  const filterByType = useCallback(
    (type: StockAdjustment['adjustmentType'] | 'all'): StockAdjustment[] => {
      if (type === 'all') return stockAdjustments;
      return stockAdjustments.filter((sa) => sa.adjustmentType === type);
    },
    [stockAdjustments]
  );

  const filterByStatus = useCallback(
    (status: StockAdjustment['status'] | 'all'): StockAdjustment[] => {
      if (status === 'all') return stockAdjustments;
      return stockAdjustments.filter((sa) => sa.status === status);
    },
    [stockAdjustments]
  );

  const filterByProduct = useCallback(
    (productId: string): StockAdjustment[] => {
      return stockAdjustments.filter((sa) => sa.productId === productId);
    },
    [stockAdjustments]
  );

  return {
    stockAdjustments,
    isLoading,
    addStockAdjustment,
    updateStockAdjustment,
    deleteStockAdjustment,
    getStockAdjustmentById,
    approveAdjustment,
    rejectAdjustment,
    filterByType,
    filterByStatus,
    filterByProduct,
  };
}

export type StockAdjustmentStore = ReturnType<typeof useStockAdjustmentStore>;
